/**
 * Soft≠Live Page175 base + Page221 Soft≠Live dual residual (register_plane || alarm_plane).
 * Mount: /ops/devices (panel tab)
 * Buses:
 *   GET /ops/devices/status (always-on Live posture · dual register || alarm)
 *   GET /devices when register_plane_allowed (+ Live q search when typing)
 *   GET /alarms when alarm_plane_allowed
 * Soft residual deleted:
 *  - useLive Soft dual-path without posture SoT
 *  - "age + s" invent when last_signal_age_s missing (||1 ··? "||1s")
 *  - bare chip list without KPI rail / re-read / dual plane
 *  - Soft brochure fleet when register plane sealed
 *  - Soft alarm chip invent when alarm plane sealed
 *  - raw API path / code paint in operator DOM
 * Honesty: Plant
 */
import { useMemo, useState } from "react";
import { html, Ico, Alert, go } from "./ui.js";
import { path, useApi } from "./api.js";
import { OpsHwMark, opsHwStripOf, opsHwFacetsOf } from "./ops-hw-mark.js";
import { boardHeadingOf, boardLabelSay } from "./operator-say.js";

const FILTERS = [
  { id: "all", label: "All", long: "All" },
  { id: "alarm", label: "Alarm", long: "Alarmed", needsAlarm: true },
  { id: "offline", label: "Off", long: "Offline" },
  { id: "camera", label: "Cam", long: "Cameras" },
  { id: "online", label: "Ans", long: "Answering" },
];

/** Soft≠Live: missing contract ops (flag-gated openapi) must not crash the desk. */
function safePath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__ops_devices_status") return "/ops/devices/status";
    if (op === "get__devices") {
      const q = (opts && opts.query) || {};
      const parts = Object.entries(q)
        .filter(([, v]) => v != null && String(v) !== "")
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
      return parts.length ? `/devices?${parts.join("&")}` : "/devices";
    }
    if (op === "get__alarms") {
      const q = (opts && opts.query) || {};
      const parts = Object.entries(q).map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
      return parts.length ? `/alarms?${parts.join("&")}` : "/alarms";
    }
    return "";
  }
}

/** Operator-facing bus errors — never paint paths, status codes, or stack text. */
export function softPlantBusErrorOf(err) {
  const n = String(err == null ? "" : err).trim().toLowerCase();
  if (!n) return "Boards could not fully refresh. Try again.";
  if (/\b403\b|forbidden|insufficient|scope/.test(n)) {
    return "Your account cannot open this board list. Ask a manager if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(n)) {
    return "Sign in again to continue.";
  }
  if (/\b429\b|too many/.test(n)) {
    return "Too many refreshes — wait a moment, then try again.";
  }
  if (/\b503\b|unavailable/.test(n)) {
    return "Site devices are busy. Try Re-read in a moment.";
  }
  if (/network|failed to fetch|timeout|aborted/.test(n)) {
    return "Network issue — try Refresh.";
  }
  if (/\/devices|\/alarms|\/ops\/|http|get |post |status_path|sql|traceback/.test(n)) {
    return "Boards could not fully refresh. Try again.";
  }
  return "Boards could not fully refresh. Try again.";
}

/** Pure: normalize selected atom (string id or device row). */
export function deviceSelIdOf(selected) {
  if (selected == null || selected === "") return "";
  if (typeof selected === "string" || typeof selected === "number") return String(selected);
  if (typeof selected === "object" && selected.id != null) return String(selected.id);
  return "";
}

/** Pure: trail clock from Live age seconds; never Soft "-1s". */
export function deviceAgeTrailOf(age) {
  const n = Number(age);
  if (!Number.isFinite(n) || n < 0) return "··?";
  if (n < 60) return Math.round(n) + "s";
  if (n < 3600) return Math.floor(n / 60) + "m";
  return Math.floor(n / 3600) + "h";
}

/** Pure: flatten GET /devices rows ··? blanks when missing (no Soft fill). */
export function opsDevicesRowsOf(pack) {
  const items = (pack && (pack.items || pack.devices)) || [];
  const rows = Array.isArray(items) ? items : [];
  const mapped = rows.map((r) => {
    if (!r) return null;
    const id = String(r.device_id || r.id || "").trim();
    if (!id) return null;
    const preview = r.status_preview || {};
    const online = !!(r.is_online === true || r.online === true
      || r.presence === "online"
      || String(r.status || "").toLowerCase() === "online");
    const ageRaw = r.last_signal_age_s != null
      ? r.last_signal_age_s
      : (r.last_heartbeat_age_s != null
        ? r.last_heartbeat_age_s
        : (r.last_updated_age_s != null ? r.last_updated_age_s : r.age));
    const age = Number.isFinite(Number(ageRaw)) ? Number(ageRaw) : -1;
    const kindGuess = String(r.kind || r.device_kind || "").toLowerCase();
    const hasCam = kindGuess === "camera"
      || !!(r.has_camera || r.camera_bound || r.cameras_count);
    const lat = r.map_lat == null ? null : Number(r.map_lat);
    const lng = r.map_lng == null ? null : Number(r.map_lng);
    const surveyed = Number.isFinite(lat) && Number.isFinite(lng);
    const human = String(r.display_label || r.map_label || r.label || "").trim();
    return {
      id,
      label: boardLabelSay({ display_label: human }, id),
      kind: hasCam ? "camera" : (kindGuess || "device"),
      zone: String(r.zone || "").trim(),
      group: String(r.notification_group || r.group || "").trim(),
      age,
      offline: !online,
      offline_reason: String(preview.disconnect_reason || "").trim(),
      surveyed,
      ll: surveyed ? [lng, lat] : null,
      claimedCaps: Array.isArray(r.claimed_capabilities || r.claimed_caps)
        ? (r.claimed_capabilities || r.claimed_caps)
        : [],
      verifiedCaps: Array.isArray(r.verified_capabilities || r.verified_caps)
        ? (r.verified_capabilities || r.verified_caps)
        : [],
      hw_code: String(r.hw_code_resolved || r.product_hw_code || "").trim(),
    };
  }).filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const d of mapped) {
    if (seen.has(d.id)) continue;
    seen.add(d.id);
    out.push(d);
  }
  return out;
}

/** Pure: alarm device ids from GET /alarms. */
export function opsAlarmsDevSetOf(pack) {
  const items = (pack && (pack.items || pack.alarms)) || [];
  const list = Array.isArray(items) ? items : [];
  return new Set(
    list.map((a) => a && (a.dev || a.device_id || a.device)).filter(Boolean).map(String),
  );
}

/** Pure: filter Live register rows. */
export function filterDevicesOf(devices, offlineIds, alarms, filter, q) {
  const list = Array.isArray(devices) ? devices : [];
  const offSet = new Set(Array.isArray(offlineIds) ? offlineIds : []);
  const alarmed = alarms instanceof Set
    ? alarms
    : new Set(
      (Array.isArray(alarms) ? alarms : []).map((a) => a && a.dev).filter(Boolean),
    );
  const needle = String(q || "").trim().toLowerCase();
  const f = String(filter || "all");
  return list.filter((d) => {
    if (!d || !d.id) return false;
    const off = offSet.has(d.id) || !!d.offline;
    if (f === "offline" && !off) return false;
    if (f === "online" && off) return false;
    if (f === "alarm" && !alarmed.has(d.id)) return false;
    if (f === "camera" && d.kind !== "camera") return false;
    if (needle) {
      const hay = [d.label, d.id, d.zone, d.group].join(" ").toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });
}

/** Pure: KPI strip. Always returns a full object (never null). */
export function devicesKpisOf(devices, offlineIds, alarms) {
  const list = Array.isArray(devices) ? devices : [];
  const offSet = new Set(Array.isArray(offlineIds) ? offlineIds : []);
  const offlineN = list.filter((d) => d && (offSet.has(d.id) || d.offline)).length;
  const alarmed = alarms instanceof Set
    ? alarms
    : new Set(
      (Array.isArray(alarms) ? alarms : []).map((a) => a && a.dev).filter(Boolean),
    );
  const alarmN = list.filter((d) => d && alarmed.has(d.id)).length;
  const cameras = list.filter((d) => d && d.kind === "camera").length;
  return {
    total: list.length,
    online: Math.max(0, list.length - offlineN),
    offline: offlineN,
    alarm: alarmN,
    cameras,
  };
}

/**
 * Pure: honest visible total while plant pack loads.
 *
 * Trap: ``Number(null) === 0`` is finite — never treat a missing pack as total:0
 * via ``Number(pack && pack.total)``, then read ``pack.total`` and throw (Observe 500).
 *
 * @param {object|null|undefined} pack  GET /devices envelope
 * @param {number} sourceN  painted row count this tick
 * @returns {number}
 */
export function plantVisibleTotalOf(pack, sourceN) {
  const rows = Number.isFinite(Number(sourceN)) ? Math.max(0, Number(sourceN)) : 0;
  if (!pack || typeof pack !== "object") return rows;
  const page = pack.page && typeof pack.page === "object" ? pack.page : null;
  if (page && page.visible_total != null) {
    const vt = Number(page.visible_total);
    if (Number.isFinite(vt) && vt >= 0) return Math.floor(vt);
  }
  if (pack.total != null) {
    const pt = Number(pack.total);
    if (Number.isFinite(pt) && pt >= 0) return Math.floor(pt);
  }
  return rows;
}

/** Soft seat label for Plant foot / chip — operator readable. */
export function softPlantSeatLabelOf(role) {
  const r = String(role || "").toLowerCase();
  if (r === "user") return "Operator";
  if (r === "admin") return "Site manager";
  if (r === "superadmin") return "Platform";
  if (r === "sa_staff") return "Staff";
  return role || "Seat";
}

/** Pure: dual seat from Live Ops Devices posture (Page221). */
export function opsDevicesSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const paintMax = Number(lim.device_paint_max);
  const alarmMax = Number(lim.alarm_paint_max);
  const registerAllowed = (p.register_plane_allowed === true || p.register_allowed === true);
  const alarmsAllowed = (p.alarm_plane_allowed === true || p.alarms_read_allowed === true);
  const searchMin = Number(p.register_live_search_min_chars);
  return {
    registerAllowed,
    alarmsAllowed,
    selectAllowed: (p.select_detail_allowed === true),
    reReadAllowed: (p.re_read_allowed === true),
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    devicePaintMax:
      Number.isFinite(paintMax) && paintMax > 0 ? Math.floor(paintMax) : 128,
    alarmPaintMax:
      Number.isFinite(alarmMax) && alarmMax > 0 ? Math.floor(alarmMax) : 100,
    liveSearchMin:
      Number.isFinite(searchMin) && searchMin > 0 ? Math.floor(searchMin) : 2,
    liveSearchEnabled: p.register_live_search_enabled === true,
    fleetScale: p.fleet_register_scale === true,
  };
}

/** Pure: stub seed for inspect when map roster missed the Plant pick. */
export function plantSelectSeedOf(row) {
  if (!row || typeof row !== "object") return null;
  const id = String(row.id || row.device_id || "").trim();
  if (!id) return null;
  return {
    id,
    label: String(row.label || row.display_label || "").trim() || boardHeadingOf(id),
    kind: String(row.kind || "device").trim() || "device",
    zone: String(row.zone || "").trim(),
    group: String(row.group || "").trim(),
    age: Number.isFinite(Number(row.age)) ? Number(row.age) : -1,
    offline: !!row.offline,
    surveyed: !!row.surveyed,
    ll: Array.isArray(row.ll) ? row.ll : null,
  };
}

export function OpsDevicesDesk({ selected, onSelect, toast }) {
  const posture = useApi(safePath("get__ops_devices_status"));
  const post = posture.data || {};
  const seat = opsDevicesSeatOf(
    posture.loading || !posture.data
      ? {
          enabled: true,
          register_plane_allowed: false,
          alarm_plane_allowed: false,
          register_allowed: false,
          alarms_read_allowed: false,
          select_detail_allowed: false,
          re_read_allowed: false,
          register_live_search_enabled: false,
          register_live_search_min_chars: 2,
          limits: { device_paint_max: 128, alarm_paint_max: 100 },
        }
      : post,
  );
  const [q, setQ] = useState("");
  const [f, setF] = useState("all");
  const [n, setN] = useState(12);
  const qTrim = String(q || "").trim();
  const liveSearch =
    seat.liveSearchEnabled && qTrim.length >= seat.liveSearchMin;
  /* Live register bus: always send paint limit (staff must not dump full fleet). */
  const devicesPath = seat.registerAllowed
    ? (liveSearch
      ? safePath("get__devices", {
        query: {
          q: qTrim,
          limit: seat.devicePaintMax,
          skip_cache: 1,
        },
      })
      : safePath("get__devices", {
        query: {
          limit: seat.devicePaintMax,
          skip_cache: 1,
        },
      }))
    : "";
  const devicesR = useApi(devicesPath);
  const alarmsR = useApi(
    seat.alarmsAllowed
      ? safePath("get__alarms", {
        query: { since_hours: 24, limit: seat.alarmPaintMax },
      })
      : "",
  );
  const selId = deviceSelIdOf(selected);

  const pack = devicesR.data;
  const rawRows = useMemo(() => opsDevicesRowsOf(pack), [pack]);
  const sourceN = rawRows.length;
  const devices = useMemo(
    () => rawRows.slice(0, seat.devicePaintMax),
    [rawRows, seat.devicePaintMax],
  );
  const paintedClamp = sourceN > devices.length;
  const visibleTotal = plantVisibleTotalOf(pack, sourceN);
  const offline = useMemo(
    () => devices.filter((d) => d.offline).map((d) => d.id),
    [devices],
  );
  const alarmed = useMemo(
    () => (seat.alarmsAllowed ? opsAlarmsDevSetOf(alarmsR.data) : new Set()),
    [alarmsR.data, seat.alarmsAllowed],
  );
  const ready = seat.registerAllowed
    ? (!!devicesR.data || !!devicesR.error) && !devicesR.loading
    : !posture.loading;
  const kpis = useMemo(
    () => devicesKpisOf(devices, offline, alarmed) || devicesKpisOf([], [], new Set()),
    [devices, offline, alarmed],
  );
  const effectiveFilter = (f === "alarm" && !seat.alarmsAllowed) ? "all" : f;
  /* When Live server search is active, server already filtered by q — avoid double miss. */
  const list = useMemo(
    () => filterDevicesOf(
      devices,
      offline,
      alarmed,
      effectiveFilter,
      liveSearch ? "" : q,
    ),
    [devices, offline, alarmed, effectiveFilter, q, liveSearch],
  );
  const busErr = devicesR.error || alarmsR.error || posture.error;
  const bothSealed = !seat.registerAllowed && !seat.alarmsAllowed;

  const reRead = () => {
    if (!seat.reReadAllowed && !posture.loading) {
      toast && toast("Refresh is closed for this seat", "warn");
      return;
    }
    posture.reload && posture.reload();
    devicesR.reload && devicesR.reload();
    alarmsR.reload && alarmsR.reload();
    toast && toast("Boards updated", "go");
  };

  const pick = (row) => {
    if (!seat.selectAllowed || !onSelect) return;
    const seed = plantSelectSeedOf(row);
    if (seed) onSelect(seed);
  };

  if (!posture.loading && (post.enabled === false || bothSealed)) {
    return html`<div
      className="ops-hud ops-plant ops-field ops-field--plant ops-dev-desk ops-dev-desk--liquid"
      data-cmc-page="page143-devices" data-cmc-legacy="page65-devices"
      data-cmc-residual="page221-ops-devices"
      data-field="v561">
      <header className="ops-hud__top">
        <div className="ops-hud__brand">
          <span className="ops-hud__chip">Boards</span>
          <span className="ops-hud__chip ops-hud__chip--live">Closed</span>
        </div>
        <button type="button" className="ops-hud__refresh"
          disabled=${!seat.reReadAllowed || posture.loading}
          onClick=${reRead} title="Refresh boards">
          <${Ico} d="refresh" w=${13}/>
        </button>
      </header>
      <p className="ops-hud__pulse">Boards closed for this account</p>
      <p className="ops-hud__quiet">You cannot open the board list here. Ask a manager if you need access.</p>
    </div>`;
  }

  const pulseLine = posture.loading
    ? "Loading boards…"
      : ready
      ? (kpis.total
        ? `${kpis.online}/${kpis.total} answering · ${kpis.offline} offline${
            paintedClamp ? " · showing the first boards" : ""
          }`
        : (liveSearch
          ? "No matches for this search."
          : "No boards yet."))
      : (seat.registerAllowed ? "Loading boards…" : "List closed");

  const seatLabel = softPlantSeatLabelOf(seat.role || post.principal_role);

  const visV = ready ? kpis.total : (seat.registerAllowed ? "…" : "—");
  const ansV = ready ? kpis.online : (seat.registerAllowed ? "…" : "—");
  const offV = ready ? kpis.offline : (seat.registerAllowed ? "…" : "—");
  const alarmV = seat.alarmsAllowed
    ? (ready || alarmsR.data || alarmsR.error ? kpis.alarm : "…")
    : "—";

  return html`<div
    className="ops-hud ops-plant ops-field ops-field--plant ops-dev-desk ops-dev-desk--liquid"
    data-cmc-page="page143-devices" data-cmc-legacy="page65-devices"
    data-cmc-residual="page221-ops-devices"
    data-field="v561"
    data-ready=${ready ? "1" : "0"}
    data-live-search=${liveSearch ? "1" : "0"}
    data-fleet=${seat.fleetScale ? "1" : "0"}>

    <div className="ops-hud__scan" aria-hidden="true" />

    <header className="ops-hud__top">
      <div className="ops-hud__brand">
        <span className="ops-hud__chip">Boards</span>
        <span className="ops-hud__chip">${ready ? kpis.total : "—"}</span>
        <span className="ops-hud__chip ops-hud__chip--seat" title="Seat">${seatLabel}</span>
        ${liveSearch
          ? html`<span className="ops-hud__chip ops-hud__chip--live">Search</span>`
          : html`<span className="ops-hud__chip ops-hud__chip--live">On site</span>`}
      </div>
      <button type="button" className="ops-hud__refresh"
        title="Refresh boards"
        disabled=${!seat.reReadAllowed && !posture.loading}
        onClick=${reRead}>
        <${Ico} d="refresh" w=${13}/>
      </button>
    </header>

    <p className="ops-hud__pulse" title="Boards">${pulseLine}</p>

    <div className="ops-hud__meters ops-plant__meters" role="group" aria-label="Board meters">
      <div className="ops-hud__meter" data-on=${kpis.total ? "1" : "0"} data-tone="go">
        <b>${visV}</b><em>Visible</em>
        <s className="ops-hud__meter-bar" style=${{ transform: `scaleX(${kpis.total ? 1 : 0.18})` }}/>
      </div>
      <div className="ops-hud__meter" data-on=${kpis.online ? "1" : "0"} data-tone="go">
        <b>${ansV}</b><em>Answering</em>
        <s className="ops-hud__meter-bar" style=${{ transform: `scaleX(${kpis.online && kpis.total ? Math.max(0.18, kpis.online / kpis.total) : 0.18})` }}/>
      </div>
      <div className="ops-hud__meter" data-on=${kpis.offline ? "1" : "0"} data-tone="warn">
        <b>${offV}</b><em>Offline</em>
        <s className="ops-hud__meter-bar" style=${{ transform: `scaleX(${kpis.offline ? 1 : 0.18})` }}/>
      </div>
      <div className="ops-hud__meter" data-on=${seat.alarmsAllowed && kpis.alarm ? "1" : "0"} data-tone="warn">
        <b>${alarmV}</b><em>Alarmed</em>
        <s className="ops-hud__meter-bar" style=${{ transform: `scaleX(${kpis.alarm ? 1 : 0.18})` }}/>
      </div>
    </div>

    ${posture.loading || (seat.registerAllowed && devicesR.loading && !devicesR.data)
      ? html`<${Alert} tone="info" title="Reading" say="Loading boards…"/>`
      : null}
    ${ready && busErr
      ? html`<${Alert} tone="warn" title="Partial" say=${softPlantBusErrorOf(busErr)}/>`
      : null}
    ${ready && paintedClamp
      ? html`<${Alert} tone="info" title="Showing a page"
          say=${`Showing first ${devices.length} of ${Math.max(visibleTotal, sourceN)} · type at least ${seat.liveSearchMin} characters to search your devices.`}/>`
      : null}
    ${seat.fleetScale && seat.liveSearchEnabled && !liveSearch && ready && !paintedClamp
      ? html`<p className="ops-hud__quiet">Large fleet seat · type an id or label (${seat.liveSearchMin}+ chars) to search your devices when the list grows.</p>`
      : null}

    <div className="ops-plant__tools">
      <label className="ops-plant__search">
        <${Ico} d="search" w=${13}/>
        <input
          value=${q}
          onChange=${(e) => { setQ(e.target.value); setN(12); }}
          placeholder=${liveSearch
            ? "Searching…"
            : "Filter · id · zone · search account after " + seat.liveSearchMin + " chars"}
          aria-label="Filter devices"
          disabled=${!seat.registerAllowed}
        />
      </label>
      <div className="ops-seg ops-plant__tabs" role="tablist" aria-label="Board filters">
        ${FILTERS.map((chip) => {
          const sealed = !!chip.needsAlarm && !seat.alarmsAllowed;
          return html`
            <button
              key=${chip.id}
              type="button"
              role="tab"
              className="ops-seg__btn ops-plant__tab"
              data-on=${effectiveFilter === chip.id ? "1" : "0"}
              disabled=${sealed || !seat.registerAllowed}
              title=${sealed ? "Alarms closed" : chip.long || chip.label}
              aria-label=${chip.long || chip.label}
              onClick=${() => { if (!sealed) { setF(chip.id); setN(12); } }}>
              ${chip.label}
            </button>`;
        })}
      </div>
    </div>

    <section className="ops-plant__roster" aria-label="Devices">
      <div className="ops-hud__lane-h">
        <p className="ops-hud__k"><s></s>Devices</p>
        <em className="ops-plant__match">
          ${ready ? list.length + " match" : (seat.registerAllowed ? "…" : "closed")}
        </em>
      </div>
      ${!seat.registerAllowed
        ? html`<div className="ops-hud__quiet"><b>List closed</b> · No inventory for this seat.</div>`
        : html`
          <div className="ops-plant__list" role="list">
            ${list.slice(0, n).map((d) => {
              const off = offline.includes(d.id) || !!d.offline;
              const trail = off ? "offline" : deviceAgeTrailOf(d.age);
              const hot = seat.alarmsAllowed && alarmed.has(d.id);
              const facets = opsHwFacetsOf(d);
              const strip = opsHwStripOf(facets.facets);
              return html`
                <button
                  key=${d.id}
                  type="button"
                  role="listitem"
                  className="ops-plant__row ops-dev-desk__row"
                  data-on=${selId === d.id ? "1" : "0"}
                  data-state=${off ? "off" : hot ? "alarm" : "on"}
                  data-surveyed=${d.surveyed ? "1" : "0"}
                  disabled=${!seat.selectAllowed}
                  onClick=${() => pick(d)}>
                  <span className="ops-plant__mark ops-dev-desk__mark" aria-hidden="true">
                    <${OpsHwMark} device=${d} size=${26}/>
                  </span>
                  <span className="ops-plant__main">
                    <b>${d.label || boardHeadingOf(d.id)}</b>
                    <u>${strip}${d.surveyed ? " · map" : " · no pos"} · ${d.zone || "—"}${d.group ? " · " + d.group : ""}</u>
                  </span>
                  <em className="ops-plant__trail" data-state=${off ? "off" : hot ? "alarm" : "on"}>${trail}</em>
                </button>`;
            })}
          </div>
          ${list.length > n
            ? html`<button type="button" className="btn btn--ghost btn--sm ops-plant__more"
                onClick=${() => setN((x) => x + 12)}>
                Show ${Math.min(12, list.length - n)} more
              </button>`
            : null}
          ${ready && !list.length
            ? html`<div className="ops-hud__quiet">
                <b>${kpis.total ? "Nothing matches" : (liveSearch ? "No matches yet" : "Register empty")}</b>
                · ${kpis.total
                  ? "Clear filter or widen search."
                  : (liveSearch
                    ? "Try another id or label."
                    : "No boards yet.")}
              </div>`
            : null}`}
    </section>

    <footer className="ops-hud__foot ops-plant__foot">
      <span className="ops-hud__foot-role">Boards · ${seatLabel}</span>
      <button type="button" className="btn btn--ghost btn--xs" onClick=${() => go("/machine?view=health")}>Machine health</button>
    </footer>
  </div>`;
}
